import * as _ from 'lodash';

import i18n from 'i18n/config';

const getT = t => (typeof t === 'function' ? t : i18n.t.bind(i18n));

export const EXCLUDE_FROM_MACHINE_PROPERTIES = {
  rotating: [],
  non_rotating: [
    'foundation',
    'orientation',
    'state_detection_enabled',
    'bluetooth_p2p_enabled',
    'alarms_type',
    'alarms_limits',
    'warning_limit',
    'critical_limit',
    'lower_bandpass_cutoff',
    'upper_bandpass_cutoff',
    'forcing_frequencies',
    // Measurement-settings keys are not deleted by this list -- it is applied
    // to machine_properties only. Non-rotating assets are protected by the
    // mcType branch in the render instead.
    'state_detection_threshold_in_gauss'
  ]
};

export const EXCLUDE_FROM_TEMPLATE_PROPERTIES = {
  rotating: [],
  non_rotating: [
    'foundation',
    'orientation',
    'state_detection_enabled',
    'bluetooth_p2p_enabled',
    'alarms_type',
    'alarms_limits',
    'warning_limit',
    'critical_limit',
    'lower_bandpass_cutoff',
    'upper_bandpass_cutoff',
    'forcing_frequencies'
  ]
};

// Mirrors the backend DeviceEventOperatorEnum. OUTSIDE_RANGE omitted: two
// disjoint ranges, and firmware takes one.
export const SPEED_RANGE_OP_GREATER_EQUAL = 2;
export const SPEED_RANGE_OP_LESS_EQUAL = 3;
export const SPEED_RANGE_OP_INSIDE = 7;

// Fallback band, for a response predating `speed_range_limits`. The API serves
// that already scaled for pole count and converted to the user's units.
export const SPEED_RANGE_MIN_HZ = 5;
export const SPEED_RANGE_MAX_HZ = 120;
export const SPEED_RANGE_DEFAULT_UNITS = 'Hz';

export const getSpeedRangeLimits = (limits) => {
  const min = limits && limits.min;
  const max = limits && limits.max;
  const units = limits && limits.units;
  return {
    min: _.isNil(min) ? SPEED_RANGE_MIN_HZ : min,
    max: _.isNil(max) ? SPEED_RANGE_MAX_HZ : max,
    units: units || SPEED_RANGE_DEFAULT_UNITS
  };
};

// UI-only inputs; they are assembled into `state_detection_speed_ranges` before saving.
export const SPEED_CONDITION_KEY = 'state_detection_speed_condition';
export const SPEED_RANGE_MIN_KEY = 'state_detection_speed_range_min';
export const SPEED_RANGE_MAX_KEY = 'state_detection_speed_range_max';
export const SPEED_RANGE_FIELD_KEYS = [
  SPEED_CONDITION_KEY,
  SPEED_RANGE_MIN_KEY,
  SPEED_RANGE_MAX_KEY
];

export const SPEED_CONDITION_NONE = 'none';
export const SPEED_CONDITION_ABOVE = 'above';
export const SPEED_CONDITION_BELOW = 'below';
export const SPEED_CONDITION_BETWEEN = 'between';

// Which bounds each condition asks for. An empty visible box is an error, never
// a way to mean "open".
export const SPEED_CONDITION_BOUNDS = {
  [SPEED_CONDITION_NONE]: [],
  [SPEED_CONDITION_ABOVE]: [SPEED_RANGE_MIN_KEY],
  [SPEED_CONDITION_BELOW]: [SPEED_RANGE_MAX_KEY],
  [SPEED_CONDITION_BETWEEN]: [SPEED_RANGE_MIN_KEY, SPEED_RANGE_MAX_KEY]
};

export const getSpeedConditionBounds = condition =>
  SPEED_CONDITION_BOUNDS[condition] || SPEED_CONDITION_BOUNDS[SPEED_CONDITION_NONE];

export const getSpeedConditionOptions = (t) => {
  const translate = getT(t);
  // No "no limit" entry: the toggle above the dropdown is what turns the
  // filter off, so an off value here would be a second way to say the same
  // thing and could disagree with the toggle.
  return [
    // Word first so it reads and is announced plainly, symbol bracketed after
    // it for anyone scanning.
    { key: SPEED_CONDITION_ABOVE,
      value: SPEED_CONDITION_ABOVE,
      text: `${translate('assets.create.machineForm.speedConditionAbove')} (\u2265)` },
    { key: SPEED_CONDITION_BELOW,
      value: SPEED_CONDITION_BELOW,
      text: `${translate('assets.create.machineForm.speedConditionBelow')} (\u2264)` },
    { key: SPEED_CONDITION_BETWEEN, value: SPEED_CONDITION_BETWEEN, text: translate('assets.create.machineForm.speedConditionBetween') }
  ];
};

// Null while a bound is missing; the form blocks the save in that state.
export const buildStateDetectionSpeedRanges = (condition, min, max) => {
  const hasMin = !_.isNil(min);
  const hasMax = !_.isNil(max);
  // A fresh array every time: backend change detection misses in-place mutation.
  if (condition === SPEED_CONDITION_ABOVE && hasMin) {
    return [{ op: SPEED_RANGE_OP_GREATER_EQUAL, values: [min] }];
  }
  if (condition === SPEED_CONDITION_BELOW && hasMax) {
    return [{ op: SPEED_RANGE_OP_LESS_EQUAL, values: [max] }];
  }
  if (condition === SPEED_CONDITION_BETWEEN && hasMin && hasMax) {
    return [{ op: SPEED_RANGE_OP_INSIDE, values: [min, max] }];
  }
  return null;
};

export const parseStateDetectionSpeedRanges = (ranges) => {
  const cleared = { condition: SPEED_CONDITION_NONE, min: null, max: null };
  if (!_.isArray(ranges) || _.isEmpty(ranges)) return cleared;
  const stored = ranges[0];
  const values = stored && stored.values;
  if (!_.isArray(values) || _.isEmpty(values)) return cleared;
  const op = stored.op;
  if (op === SPEED_RANGE_OP_GREATER_EQUAL) {
    return { condition: SPEED_CONDITION_ABOVE, min: values[0], max: null };
  }
  if (op === SPEED_RANGE_OP_LESS_EQUAL) {
    return { condition: SPEED_CONDITION_BELOW, min: null, max: values[0] };
  }
  if (op === SPEED_RANGE_OP_INSIDE && values.length > 1) {
    return { condition: SPEED_CONDITION_BETWEEN, min: values[0], max: values[1] };
  }
  // An operator this form cannot express reads as no filter.
  return cleared;
};

export const COMPONENT_TYPE_UPDATE = {
  confirmation: 'machineBuilder.componentTypeUpdate.confirmation',
  select_req: 'machineBuilder.componentTypeUpdate.selectReq'
};

export const MEASUREMENT_INTERVAL = {
  overall: 10800,
  full_bandwidth: 43200,
  high_resolution: 43200
};

export const MEASUREMENT_TYPE = {
  overall: '0',
  full_bandwidth: '1',
  high_resolution: '2'
};

export const AGGRESSIVE_SETTINGS = {
  num_aggressive_readings: 3,
  aggressive_reading_interval: 300
};

export const getNumAggressiveReadings = (t) => {
  const tr = getT(t);
  return [
    { text: tr('machineBuilder.constants.aggressiveReadings.one', { defaultValue: '1 times' }), value: 1 },
    { text: tr('machineBuilder.constants.aggressiveReadings.two', { defaultValue: '2 times' }), value: 2 },
    { text: tr('machineBuilder.constants.aggressiveReadings.three', { defaultValue: '3 times' }), value: 3 },
    { text: tr('machineBuilder.constants.aggressiveReadings.four', { defaultValue: '4 times' }), value: 4 },
    { text: tr('machineBuilder.constants.aggressiveReadings.five', { defaultValue: '5 times' }), value: 5 },
    { text: tr('machineBuilder.constants.aggressiveReadings.six', { defaultValue: '6 times' }), value: 6 }
  ];
};

export const NUM_AGGRESSIVE_READINGS = getNumAggressiveReadings();

export const getAggressiveIntervals = (t) => {
  const tr = getT(t);
  return [
    { text: tr('machineBuilder.constants.aggressiveInterval.every3Minutes', { defaultValue: 'Every 3 minutes' }), value: 180 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every4Minutes', { defaultValue: 'Every 4 minutes' }), value: 240 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every5Minutes', { defaultValue: 'Every 5 minutes' }), value: 300 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every10Minutes', { defaultValue: 'Every 10 minutes' }), value: 600 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every15Minutes', { defaultValue: 'Every 15 minutes' }), value: 900 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every30Minutes', { defaultValue: 'Every 30 minutes' }), value: 1800 },
    { text: tr('machineBuilder.constants.aggressiveInterval.every1Hour', { defaultValue: 'Every 1 hour' }), value: 3600 }
  ];
};

export const AGGRESSIVE_INTERVALS = getAggressiveIntervals();

export const getAdditionalAggressiveMeasurementIntervals = (t) => {
  const tr = getT(t);
  return [
    { text: tr('machineBuilder.constants.additionalAggressiveInterval.onceTwoDays', { defaultValue: 'Once in two days' }), value: 172800 },
    { text: tr('machineBuilder.constants.additionalAggressiveInterval.onceThreeDays', { defaultValue: 'Once in three days' }), value: 259200 },
    { text: tr('machineBuilder.constants.additionalAggressiveInterval.onceFourDays', { defaultValue: 'Once in four days' }), value: 345600 },
  ];
};

export const ADDITIONAL_AGGRESSIVE_MEASUREMENT_INTERVALS = getAdditionalAggressiveMeasurementIntervals();

const MEASUREMENT_INTERVAL_VALUE_KEYS = {
  // Aggressive-measurement timeout only; no other interval field uses value 0.
  0: 'neverTimeout',
  180: 'every3Minutes',
  240: 'every4Minutes',
  300: 'every5Minutes',
  600: 'every10Minutes',
  900: 'every15Minutes',
  1800: 'every30Minutes',
  3600: 'every1Hour',
  10800: 'every3Hours',
  21600: 'every6Hours',
  43200: 'every12Hours',
  86400: 'onceADay',
  604800: 'onceAWeek',
  172800: 'onceTwoDays',
  259200: 'onceThreeDays',
  345600: 'onceFourDays',
  432000: 'onceFiveDays',
};

const MEASUREMENT_INTERVAL_TEXT_KEYS = {
  // AggressiveMeasurementTimeoutEnum emits bare DURATIONS ("15 minutes" = "times out
  // after 15 minutes"), unlike every other interval enum, which prefixes "Every"/"Once".
  // These must not resolve to the every-N-minutes frequency wording.
  'Never timeout': 'neverTimeout',
  '15 minutes': 'fifteenMinutes',
  '30 minutes': 'thirtyMinutes',
  '1 hour': 'oneHour',
  'Every 3 minutes': 'every3Minutes',
  'Every 4 minutes': 'every4Minutes',
  'Every 5 minutes': 'every5Minutes',
  'Every 10 minutes': 'every10Minutes',
  'Every 15 minutes': 'every15Minutes',
  'Every 30 minutes': 'every30Minutes',
  'Every hour': 'every1Hour',
  'Every 1 hour': 'every1Hour',
  'Every 3 hours': 'every3Hours',
  'Every 3 Hours': 'every3Hours',
  'Every 6 hours': 'every6Hours',
  'Every 6 Hours': 'every6Hours',
  'Every 12 hours': 'every12Hours',
  'Every 12 Hours': 'every12Hours',
  'Once a day': 'onceADay',
  'Once a Day': 'onceADay',
  'Once a week': 'onceAWeek',
  'Once a Week': 'onceAWeek',
  'Once in two days': 'onceTwoDays',
  'Once in three days': 'onceThreeDays',
  'Once in four days': 'onceFourDays',
  'Once in 2 days': 'onceTwoDays',
  'Once in 2 Days': 'onceTwoDays',
  'Once in 3 days': 'onceThreeDays',
  'Once in 3 Days': 'onceThreeDays',
  'Once in 4 days': 'onceFourDays',
  'Once in 4 Days': 'onceFourDays',
  'Once in 5 days': 'onceFiveDays',
  'Once in 5 Days': 'onceFiveDays',
};

const MEASUREMENT_INTERVAL_I18N_NAMESPACES = [
  'machineBuilder.constants.measurementInterval',
  'machineBuilder.constants.aggressiveInterval',
  'machineBuilder.constants.additionalAggressiveInterval',
];

const STATE_DETECTION_INTERVAL_VALUE_KEYS = {
  300: 'fiveMinutes',
  900: 'fifteenMinutes',
  1800: 'thirtyMinutes',
  3600: 'oneHour',
  7200: 'twoHours',
  10800: 'threeHours',
  14400: 'fourHours',
  21600: 'sixHours',
  28800: 'eightHours',
  43200: 'twelveHours',
  86400: 'twentyFourHours',
};

const STATE_DETECTION_INTERVAL_TEXT_KEYS = {
  '5 Minutes': 'fiveMinutes',
  '5 minutes': 'fiveMinutes',
  '15 Minutes': 'fifteenMinutes',
  '15 minutes': 'fifteenMinutes',
  '30 Minutes': 'thirtyMinutes',
  '30 minutes': 'thirtyMinutes',
  '1 Hour': 'oneHour',
  '1 hour': 'oneHour',
  '2 Hours': 'twoHours',
  '2 hours': 'twoHours',
  '3 Hours': 'threeHours',
  '3 hours': 'threeHours',
  '4 Hours': 'fourHours',
  '4 hours': 'fourHours',
  '6 Hours': 'sixHours',
  '6 hours': 'sixHours',
  '8 Hours': 'eightHours',
  '8 hours': 'eightHours',
  '12 Hours': 'twelveHours',
  '12 hours': 'twelveHours',
  '24 Hours': 'twentyFourHours',
  '24 hours': 'twentyFourHours',
};

export const resolveStateDetectionIntervalKey = (opt) => {
  if (opt == null) return null;
  // Same guard as resolveMeasurementIntervalKey: reject false/[]/' ', which all
  // coerce to 0 and would match a numeric key.
  const isNumericValue = typeof opt.value === 'number'
    || (typeof opt.value === 'string' && opt.value.trim() !== '');
  if (isNumericValue && STATE_DETECTION_INTERVAL_VALUE_KEYS[opt.value]) {
    return STATE_DETECTION_INTERVAL_VALUE_KEYS[opt.value];
  }
  if (typeof opt.text === 'string' && STATE_DETECTION_INTERVAL_TEXT_KEYS[opt.text]) {
    return STATE_DETECTION_INTERVAL_TEXT_KEYS[opt.text];
  }
  return null;
};

export const getStateDetectionIntervalDisplayText = (t, opt) => {
  if (!opt) return '';
  const tr = getT(t);
  const intervalKey = resolveStateDetectionIntervalKey(opt);
  if (!intervalKey) return opt.text;

  return tr(`machineBuilder.constants.stateDetectionInterval.${intervalKey}`, {
    defaultValue: opt.text,
  });
};

export const translateStateDetectionIntervalOptions = (t, options) => {
  if (!Array.isArray(options)) return [];
  return options.map(opt => ({
    ...opt,
    text: getStateDetectionIntervalDisplayText(t, opt),
  }));
};

export const resolveMeasurementIntervalKey = (opt) => {
  if (opt == null) return null;
  // Text is matched FIRST: the same numeric value means different things across
  // fields (3600 is "Every 1 hour" as a frequency but "1 hour" as a timeout), and
  // only the text disambiguates. Values are the fallback for unrecognised text.
  if (typeof opt.text === 'string' && MEASUREMENT_INTERVAL_TEXT_KEYS[opt.text]) {
    return MEASUREMENT_INTERVAL_TEXT_KEYS[opt.text];
  }
  // Only genuine numbers or numeric strings; guards against false/[]/' ' coercing to 0.
  const isNumericValue = typeof opt.value === 'number'
    || (typeof opt.value === 'string' && opt.value.trim() !== '');
  const numericValue = Number(opt.value);
  if (
    isNumericValue
    && !Number.isNaN(numericValue)
    && MEASUREMENT_INTERVAL_VALUE_KEYS[numericValue]
  ) {
    return MEASUREMENT_INTERVAL_VALUE_KEYS[numericValue];
  }
  if (typeof opt.text === 'string' && opt.text) {
    return opt.text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
  return null;
};

export const getMeasurementIntervalDisplayText = (t, opt) => {
  if (!opt) return '';
  const tr = getT(t);
  // Prefer a key resolved on a previous pass: `text` may already be localized.
  const intervalKey = opt.intervalKey || resolveMeasurementIntervalKey(opt);
  if (!intervalKey) return opt.text;

  for (let i = 0; i < MEASUREMENT_INTERVAL_I18N_NAMESPACES.length; i += 1) {
    const fullKey = `${MEASUREMENT_INTERVAL_I18N_NAMESPACES[i]}.${intervalKey}`;
    const translated = tr(fullKey, { defaultValue: '' });
    if (translated && translated !== fullKey) return translated;
  }

  return tr(`machineBuilder.constants.measurementInterval.${intervalKey}`, {
    defaultValue: opt.text,
  });
};

export const translateMeasurementIntervalOptions = (t, options) => {
  if (!Array.isArray(options)) return [];
  return options.map((opt) => {
    // Carry the resolved key so a second pass re-translates from it rather than from
    // the already-localized `text`. Without this the function is not idempotent: in ja
    // a timeout's "15分" would miss the text map and fall back to the VALUE map, which
    // means the frequency wording ("15分ごと") — a different meaning.
    const intervalKey = opt.intervalKey || resolveMeasurementIntervalKey(opt);
    return {
      ...opt,
      intervalKey,
      text: getMeasurementIntervalDisplayText(t, { ...opt, intervalKey }),
    };
  });
};
